import { t } from "adapters/i18n";
import type { CauseOfDeath } from "shared/types";
import type { DataResult, ServiceConstructor } from "../services.types";

export function CauseOfDeathServiceMixin<TBase extends ServiceConstructor>(
  Base: TBase
) {
  return class CauseOfDeathService extends Base {
    readonly causeOfDeathUrl = `${process.env.REACT_APP_ENDPOINT}herds/causeDeath`;

    async fetchFarmCausesOfDeath(
      farmId: number,
      withDeleted = true
    ): Promise<DataResult<CauseOfDeath>> {
      try {
        const urlParams = this.montaURLParametros({ farmId, withDeleted });
        const url = this.montaURL(this.causeOfDeathUrl, urlParams);
        const result = await this.get(url);

        if (result.data?.data == null) {
          return { data: [], total: 0 };
        }

        const data = result.data.data as CauseOfDeath[];
        const total = result.data.total as number;

        return { data, total };
      } catch (_) {
        throw new Error(t("livestock:fetch-cause-of-death-error"));
      }
    }

    async createCauseOfDeath(
      payload: Pick<CauseOfDeath, "farmId" | "name">
    ): Promise<CauseOfDeath> {
      try {
        const result = await this.post(this.causeOfDeathUrl, payload);
        return result.data.data;
      } catch (_) {
        throw new Error(t("livestock:create-cause-of-death-error"));
      }
    }

    async deleteCauseOfDeathById(id: number): Promise<void> {
      try {
        await this.delete(`${this.causeOfDeathUrl}/${id}/disable`);
      } catch (_) {
        throw new Error(t("livestock:delete-cause-of-death-error"));
      }
    }

    async activateCauseOfDeath(
      id: number,
      payload: Pick<CauseOfDeath, "name">
    ): Promise<CauseOfDeath> {
      try {
        const result = await this.patch(
          `${this.causeOfDeathUrl}/${id}/enable`,
          payload
        );
        return result.data.data;
      } catch (_) {
        throw new Error(t("livestock:activate-cause-of-death-error"));
      }
    }

    async updateCauseOfDeath(
      id: number,
      payload: Partial<Pick<CauseOfDeath, "name">>
    ): Promise<CauseOfDeath> {
      try {
        const result = await this.patch(
          `${this.causeOfDeathUrl}/${id}`,
          payload
        );
        return result.data.data as CauseOfDeath;
      } catch (_) {
        throw new Error(t("livestock:update-cause-of-death-error"));
      }
    }
  };
}
